<template>
  <Content>

  <section class="contact-section py-4 py-lg-5 px-3 px-md-4">
    <div class="container-fluid">
      
      <!-- تیتر و هدر بخش -->
      <div class="text-center mb-4 mb-lg-5" :dir="lang === 'fa' ? 'rtl' : 'ltr'">
        <div class="section-badge d-inline-flex align-items-center gap-2 mb-2">
          <i class="bi bi-chat-heart-fill"></i>
          <span>{{ currentLang.badge }}</span>
        </div>
        <h1 class="section-title">{{ currentLang.title }}</h1>
        <p class="section-desc text-muted mx-auto">{{ currentLang.subtitle }}</p>
      </div>

      <div class="row g-4 align-items-stretch" :dir="lang === 'fa' ? 'rtl' : 'ltr'">
        
        <!-- ستون اول: فرم ارسال پیام -->
        <div class="col-12 col-lg-7">
          <div class="contact-card h-100 p-4 p-md-5">
            <div class="d-flex align-items-center gap-3 mb-4">
              <div class="icon-circle bg-teal-soft">
                <i class="bi bi-envelope-paper-heart-fill text-teal"></i>
              </div>
              <div>
                <h3 class="card-title mb-1">{{ currentLang.formHeaderTitle }}</h3>
                <p class="card-subtitle text-muted mb-0">{{ currentLang.formHeaderSub }}</p>
              </div>
            </div>

            <!-- فرم -->
            <form @submit.prevent="handleSubmit" novalidate>
              <div class="row g-3">
                
                <!-- فیلد نام و نام خانوادگی -->
                <div class="col-12 col-md-6">
                  <label class="form-label fw-bold">
                    {{ currentLang.fullNameLabel }}
                    <span class="text-danger">*</span>
                  </label>
                  <div class="input-group custom-input-group">
                    <span class="input-group-text"><i class="bi bi-person"></i></span>
                    <input
                      v-model.trim="form.fullname"
                      type="text"
                      class="form-control"
                      :placeholder="currentLang.fullNamePlaceholder"
                      required
                    />
                  </div>
                </div>

                <!-- فیلد شماره تماس -->
                <div class="col-12 col-md-6">
                  <label class="form-label fw-bold">
                    {{ currentLang.phoneLabel }}
                    <span class="text-danger">*</span>
                  </label>
                  <div class="input-group custom-input-group">
                    <span class="input-group-text"><i class="bi bi-telephone"></i></span>
                    <input
                      v-model.trim="form.phone"
                      type="tel"
                      dir="ltr"
                      class="form-control"
                      :placeholder="currentLang.phonePlaceholder"
                      required
                    />
                  </div>
                </div>

                <!-- فیلد موضوع (اختیاری) -->
                <div class="col-12">
                  <label class="form-label fw-bold">{{ currentLang.subjectLabel }}</label>
                  <div class="input-group custom-input-group">
                    <span class="input-group-text"><i class="bi bi-tag"></i></span>
                    <select v-model="form.subject" class="form-select">
                      <option value="support">{{ currentLang.subjects.support }}</option>
                      <option value="payment">{{ currentLang.subjects.payment }}</option>
                      <option value="courses">{{ currentLang.subjects.courses }}</option>
                      <option value="other">{{ currentLang.subjects.other }}</option>
                    </select>
                  </div>
                </div>

                <!-- فیلد متن پیام -->
                <div class="col-12">
                  <label class="form-label fw-bold">
                    {{ currentLang.messageLabel }}
                    <span class="text-danger">*</span>
                  </label>
                  <div class="input-group custom-input-group">
                    <span class="input-group-text align-items-start pt-2"><i class="bi bi-chat-left-text"></i></span>
                    <textarea
                      v-model.trim="form.message"
                      rows="4"
                      class="form-control"
                      :placeholder="currentLang.messagePlaceholder"
                      required
                    ></textarea>
                  </div>
                </div>

                <!-- دکمه ارسال و فیدبک وضعیت -->
                <div class="col-12 mt-4">
                  <!-- پیام هشدار خطا یا موفقیت -->
                  <div v-if="statusMessage.text" class="alert mb-3" :class="statusMessage.type === 'success' ? 'alert-success' : 'alert-danger'">
                    <i class="bi me-2" :class="statusMessage.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
                    {{ statusMessage.text }}
                  </div>

                  <button
                    type="submit"
                    class="btn btn-submit w-100 py-3 d-flex align-items-center justify-content-center gap-2"
                    :disabled="isLoading"
                  >
                    <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="bi bi-send-fill"></i>
                    <span>{{ isLoading ? currentLang.sendingBtn : currentLang.submitBtn }}</span>
                  </button>
                </div>

              </div>
            </form>

          </div>
        </div>

        <!-- ستون دوم: اطلاعات تماس و نقشه -->
        <div class="col-12 col-lg-5 d-flex flex-column gap-3">
          
          <!-- کارت اطلاعات سریع -->
          <div class="contact-card p-4">
            <h4 class="card-title fs-6 mb-3">{{ currentLang.quickInfoTitle }}</h4>
            
            <div class="d-flex flex-column gap-3">
              <div class="d-flex align-items-center gap-3 info-item">
                <div class="info-icon"><i class="bi bi-geo-alt-fill text-danger"></i></div>
                <div>
                  <small class="text-muted d-block">{{ currentLang.addressTitle }}</small>
                  <span class="info-val">{{ currentLang.addressVal }}</span>
                </div>
              </div>

              <div class="d-flex align-items-center gap-3 info-item">
                <div class="info-icon"><i class="bi bi-telephone-fill text-primary"></i></div>
                <div>
                  <small class="text-muted d-block">{{ currentLang.phoneTitle }}</small>
                  <a href="tel:02112345678" class="info-val text-decoration-none" dir="ltr">09190319551</a>
                </div>
              </div>

              <div class="d-flex align-items-center gap-3 info-item">
                <div class="info-icon"><i class="bi bi-clock-fill text-warning"></i></div>
                <div>
                  <small class="text-muted d-block">{{ currentLang.hoursTitle }}</small>
                  <span class="info-val">{{ currentLang.hoursVal }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- نقشه شرکت -->
          <div class="contact-card p-2 flex-grow-1 overflow-hidden d-flex flex-column">
            <div class="map-container flex-grow-1 rounded-4 overflow-hidden position-relative">
              <iframe
                title="Office Location Map"
                :src="mapEmbedUrl"
                width="100%"
                height="100%"
                style="border:0; min-height: 230px;"
                allowfullscreen=""
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

        </div>

      </div>

    </div>
  </section>


    <footerAdmin></footerAdmin>
  </Content>
</template>

<script setup>
import { reactive, ref,computed } from "vue";

import Content from "@/components/AdminContent.vue";
import footerAdmin from "@/components/AdminFooter.vue";


import { lang } from "@/plugins/lang";

import enText from "@/locales/en/contactPage";
import faText from "@/locales/fa/contactPage";

const translations = {
  en: enText,
  fa: faText,
};

const currentLang = computed(() => translations[lang.value]);




// لینک تستی نقشه گوگل
const mapEmbedUrl = ref(
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3239.962828698056!2d51.40875327641884!3d35.70252877257929!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDQyJzA5LjEiTiA1McKwMjQnMzkuNCJF!5e0!3m2!1sen!2s!4v1690000000000!5m2!1sen!2s"
);

// فرم استیت
const form = reactive({
  fullname: "",
  phone: "",
  subject: "support",
  message: "",
});

const isLoading = ref(false);
const statusMessage = reactive({
  type: "",
  text: "",
});

// ارسال فرم به اندپوینت تستی
const handleSubmit = async () => {
  if (!form.fullname || !form.phone || !form.message) {
    statusMessage.type = "error";
    statusMessage.text =
      lang.value === "fa"
        ? "لطفاً تمام فیلدهای ستاره‌دار را پر نمایید."
        : "Please fill out all required fields.";
    return;
  }

  isLoading.value = true;
  statusMessage.text = "";

  try {
    const API_ENDPOINT = "https://jsonplaceholder.typicode.com/posts";

    const response = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(form),
    });

    if (!response.ok) throw new Error("Server Error");

    statusMessage.type = "success";
    statusMessage.text =
      lang.value === "fa"
        ? "پیام شما با موفقیت ارسال شد. کارشناسان ما به زودی با شما تماس خواهند گرفت."
        : "Your message has been sent successfully. We will contact you soon.";

    // پاکسازی فرم بعد از ارسال موفق
    form.fullname = "";
    form.phone = "";
    form.message = "";
    form.subject = "support";
  } catch (error) {
    statusMessage.type = "error";
    statusMessage.text =
      lang.value === "fa"
        ? "متاسفانه مشکلی در ارسال پیام رخ داد. لطفاً مجدداً تلاش نمایید."
        : "An error occurred while sending. Please try again.";
  } finally {
    isLoading.value = false;
  }
};




</script>



<style scoped>
.section-badge {
  background: rgba(0, 205, 196, 0.12);
  color: #008f89;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 6px 14px;
  border-radius: 50px;
}

.section-title {
  font-size: 1.85rem;
  font-weight: 800;
  color: #18325b;
}

.section-desc {
  max-width: 650px;
  font-size: 0.95rem;
  line-height: 1.7;
}

/* کارت‌های سفید */
.contact-card {
  background: #ffffff;
  border: 1px solid #eef2f6;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
}

.card-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #18325b;
}

.card-subtitle {
  font-size: 0.85rem;
}

.icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
}

.bg-teal-soft {
  background: #e6fffa;
}

.text-teal {
  color: #00a8a0;
}

/* استایل فیلدهای ورودی */
.custom-input-group {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.25s ease;
}

.custom-input-group:focus-within {
  border-color: #00cdc4;
  box-shadow: 0 0 0 3px rgba(0, 205, 196, 0.15);
}

.custom-input-group .input-group-text {
  background: #f8fafc;
  border: none;
  color: #94a3b8;
  font-size: 1.1rem;
  padding-inline: 14px;
}

.custom-input-group .form-control,
.custom-input-group .form-select {
  border: none;
  padding: 12px 14px;
  font-size: 0.9rem;
  box-shadow: none;
}

.custom-input-group .form-control:focus,
.custom-input-group .form-select:focus {
  background: #ffffff;
}

/* دکمه ارسال */
.btn-submit {
  background: linear-gradient(135deg, #00cdc4, #009688);
  border: none;
  border-radius: 14px;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  box-shadow: 0 8px 20px rgba(0, 205, 196, 0.25);
  transition: all 0.3s ease;
}

.btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #00b3aa, #00796b);
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 205, 196, 0.35);
  color: #ffffff;
}

/* آیتم‌های اطلاعات تماس */
.info-icon {
  width: 40px;
  height: 40px;
  background: #f8fafc;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.info-val {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e293b;
}

.map-container {
  min-height: 220px;
}

@media (max-width: 575.98px) {
  .contact-card {
    padding: 20px 16px;
  }
  .section-title {
    font-size: 1.4rem;
  }
}
</style>


































































 

